import React from 'react';
import {
    Box,
    Text,
    SimpleGrid,
    useColorModeValue,
    Spinner,
    Flex,
    IconButton,
    Tooltip,
} from '@chakra-ui/react';

// Components
import MiniStatistics from 'components/card/MiniStatistics';
import IconBox from 'components/icons/IconBox';
import Icon from 'components/icons/Icon';
import RelationshipStats from 'components/dashboard/RelationshipStats';
import BrandingSettings from '../branding/BrandingSettings';

// Icons
import {
    MdPeople,
    MdGroup,
    MdLink,
    MdRefresh,
    MdTrendingUp,
} from 'react-icons/md';

// Hooks
import { useAgencyDashboard } from '../../../services/useAgencyDashboard';

export default function AgencyDashboard() {
    // Chakra Color Mode
    const brandColor = useColorModeValue('brand.500', 'white');
    const boxBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');

    // Dashboard statistics
    const {
        totalTrainers,
        totalActiveClients,
        totalRelationships,
        avgClientsPerTrainer,
        isLoading,
        error,
        refreshStats,
    } = useAgencyDashboard();

    return (
        <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
            <Flex justify="right" align="center" mb="20px">
                <Tooltip label="Refresh Statistics">
                    <IconButton
                        aria-label="Refresh statistics"
                        icon={isLoading ? <Spinner size="sm" /> : <Icon as={MdRefresh} />}
                        variant="outline"
                        onClick={refreshStats}
                        isLoading={isLoading}
                        size="sm"
                    />
                </Tooltip>
            </Flex>

            <SimpleGrid
                columns={{ base: 1, md: 2, lg: 4, '2xl': 4 }}
                gap="20px"
                mb="20px"
            >
                <MiniStatistics
                    startContent={
                        <IconBox
                            w="56px"
                            h="56px"
                            bg={boxBg}
                            icon={
                                <Icon width="32px" height="32px" as={MdPeople} color={brandColor} />
                            }
                        />
                    }
                    name="Total Trainers"
                    value={isLoading ? '-' : totalTrainers.toString()}
                />
                <MiniStatistics
                    startContent={
                        <IconBox
                            w="56px"
                            h="56px"
                            bg={boxBg}
                            icon={
                                <Icon
                                    width="32px"
                                    height="32px"
                                    as={MdGroup}
                                    color={brandColor}
                                />
                            }
                        />
                    }
                    name="Active Clients"
                    value={isLoading ? '-' : totalActiveClients.toString()}
                />
                <MiniStatistics
                    startContent={
                        <IconBox
                            w="56px"
                            h="56px"
                            bg={boxBg}
                            icon={
                                <Icon
                                    width="32px"
                                    height="32px"
                                    as={MdLink}
                                    color={brandColor}
                                />
                            }
                        />
                    }
                    name="Total Relationships"
                    value={isLoading ? '-' : totalRelationships.toString()}
                />
                <MiniStatistics
                    startContent={
                        <IconBox
                            w="56px"
                            h="56px"
                            bg={boxBg}
                            icon={
                                <Icon
                                    width="32px"
                                    height="32px"
                                    as={MdTrendingUp}
                                    color={brandColor}
                                />
                            }
                        />
                    }
                    name="Avg Clients/Trainer"
                    value={isLoading ? '-' : avgClientsPerTrainer.toString()}
                />
            </SimpleGrid>

            {error && (
                <Box
                    p="20px"
                    bg={useColorModeValue('red.50', 'red.900')}
                    borderRadius="20px"
                    mb="20px"
                    border="1px solid"
                    borderColor={useColorModeValue('red.200', 'red.700')}
                >
                    <Text color={useColorModeValue('red.600', 'red.200')} fontWeight="medium">
                        Error loading dashboard statistics: {error}
                    </Text>
                </Box>
            )}

            <BrandingSettings />
        </Box>
    );
}
